import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {Location} from '@reach/router';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowDown} from '@fortawesome/pro-regular-svg-icons/faArrowDown';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons/faCartPlus';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBeacon from 'utils/useBeacon';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import ScrollingVideo from 'components/ComponentLibrary/ScrollingVideo';
import CheckoutButton from 'components/CheckoutButton';
import {SalesDisclaimer, TreesForTheFuture} from 'components/SalesCta';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import TestimonialCL from 'components/ComponentLibrary/TestimonialCL';
import Footer from 'components/Footer';

//@ts-ignore
import {testimonialsCL} from 'data/testimonials.json';
//@ts-ignore
import {ahSpeakers, recipes} from 'data/wlc-lc.json';
import ImageCardCL from 'components/ComponentLibrary/ImageCardCL';
import {GuaranteeBox} from 'components/Guarantee';
import ExitModal from 'components/ComponentLibrary/ExitModal';

export default function (props) {
  return <Location>{({location}) => <ChallengeWelcome location={location} {...props} />}</Location>;
}

function ChallengeWelcome({
  pageTitle = 'Welcome to the Eat The Rainbow Challenge',
  pageDescription = 'Join thousands of individuals saying YES to adding more color to their plates for increased nutrients.',
  ogUrl = 'https://wlc.foodrevolution.org/challenge/',
  ogImage = 'https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/wlc/challenge/og/wlc-lc-202504-open-graph-challenge.png',
  location = {},
  ...props
}) {
  const {
    phases: {
      challenge: challengeSchedule = {
        phase: 'before',
        salesEndDate: ''
      }
    }
  } = usePhaseState();
  const userState = useUserState();

  const dollarPrice = 1;
  const fullPriceYearly = 497;
  const fullPriceMonthly = 49;
  const discountPriceYearly = 197;
  const discountPriceMonthly = 29;
  const discountPennies = 0.54;
  const discountDollars = 17;
  const discountPercent = 60;

  const isChallenge = challengeSchedule.phase != 'after';

  const salesVersion = isChallenge ? 'salesDollar' : 'salesFull';
  const isDollarSale = salesVersion === 'salesDollar';

  const productSku = 'WLC';
  const productSlug = isDollarSale ? 'wlc-14daytrial-promo-special-197' : '497-wlc';
  const productValue = isDollarSale ? dollarPrice : fullPriceYearly;

  const videoUrl = 'https://www.youtube.com/watch?v=UWELrX67ChU';

  function WelcomeCheckoutButton({className = undefined, ...props}) {
    return (
      <CheckoutButton className={className} sku={productSku} price={productValue} slug={productSlug}>
        <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> Join For ${dollarPrice}
      </CheckoutButton>
    );
  }

  function CtaSection({
    id,
    headline = undefined,
    missionHeadline = undefined,
    shortened = false,
    trees = false,
    ...props
  }) {
    return (
      <SectionCL id={id} color="purple" {...props}>
        <Container>
          {headline && <div className="cta-headline text-center text-white">{headline}</div>}
          <Row className="d-flex justify-content-center">
            <Col lg="10" xl="8">
              <div className="box-shadow-black-50 rounded">
                {!shortened && (
                  <div className="background-black text-white rounded-top p-3 p-lg-4">
                    <h3 className="text-uppercase text-center m-0">Get Started Now!</h3>
                  </div>
                )}
                <div
                  className={classNames(
                    `background-white px-4 px-lg-5 py-4`,
                    shortened ? `rounded py-lg-5` : `rounded-bottom`
                  )}
                >
                  {!shortened && (
                    <>
                      <h3 className="text-center text-green mt-0 mb-4">Join For Just $1 Today</h3>
                      <p>Here’s what you’ll get as soon as your membership begins:</p>
                      <IconListCL>
                        <>Six delicious, plant-powered recipes every week</>
                        <>Money-saving tips on ways to make a whole food diet inexpensive</>
                        <>
                          Weekly health-boosting updates from Ocean Robbins and friends, delivered right to your inbox
                        </>
                        <>
                          Monthly Action Hours with today’s top health revolutionaries so you’re in the know on the
                          latest scientific breakthroughs
                        </>
                        <>
                          Action Checklists to help you put everything you discover into action — then keep you on
                          track!
                        </>
                        <>A private, moderated community of like-minded peers that support your success</>
                        <>Access to health coaches and dieticians who can help guide you if you get stuck</>
                        <>
                          Access to the entire library of 150+ videos. Plus our recipe bank of tested and beloved
                          recipes
                        </>
                        <>Locked-in rate so your membership fees will never increase</>
                        <>Easy-to-navigate membership site — everything you need to succeed is at your fingertips!</>
                        <>
                          <b>
                            SPECIAL ONE-TIME OFFER: Join from this page only and get the Brain Power Collection ($47
                            Value) for FREE!
                          </b>
                        </>
                      </IconListCL>
                    </>
                  )}
                  <div className="text-center">
                    <h3 className="section-heading text-green mb-1">Only ${dollarPrice} for a 14-day trial</h3>
                    <h3 className="mt-0 mb-2 text-green">
                      Then <del className="delete-red">${fullPriceYearly}</del> ${discountPriceYearly}/year
                    </h3>
                    <h5 className="text-400 mt-0 mb-3">(That’s just ${discountPennies} a day)</h5>
                    <p className="text-800">Or ${discountPriceMonthly}/month</p>
                    <WelcomeCheckoutButton className="offer-box-button" />
                    <SalesDisclaimer noGuaranteeText />
                    <p className="balance-text text-small mt-4 mb-0">
                      Join WHOLE Life Club for just ${dollarPrice} for your first month. After that, you’ll be enrolled
                      for a special membership price of just ${discountPriceYearly} a year (that comes to $
                      {discountPennies} per day) unless you cancel before your 14-day trial is up. Your purchase is
                      protected by our 60-day money-back guarantee.
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              {missionHeadline && <div className="text-center text-white">{missionHeadline}</div>}
              {trees && (
                <div className="mt-4">
                  <Row className="trees-for-future text-white d-flex align-items-center justify-content-center">
                    <Col xs="11" lg="8" className="mb-4 mb-lg-0">
                      <h6 className="section-only-heading text-center text-lg-start text-400">
                        For every new order, we make a donation to Trees for the Future, enabling another organic fruit
                        or nut tree to be planted in a low-income community.
                      </h6>
                    </Col>
                    <Col xs="11" lg="3" className="text-center">
                      <img
                        className={classNames(`logo-trees`)}
                        alt="Trees for the Future logo"
                        src="https://cdn.foodrevolution.org/global/trees-logo-white.png"
                        width={1080}
                        height={442}
                        style={{maxHeight: '100px', width: 'auto'}}
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </SectionCL>
    );
  }

  useBeacon();
  useBalanceText();
  return (
    <div className="page page-nosocial">
      <PageMeta title={pageTitle} description={pageDescription} ogImage={ogImage} url={ogUrl} />
      <div className="page-content">
        <SectionCL
          id="welcome-header"
          color="black"
          className="text-white text-center"
          imgUrl="https://cdn.foodrevolution.org/eattherainbow/images/1129818010-lg.jpg"
          imgDescription="colorful vegetables and fruits"
          order="first"
          angle="none"
        >
          <Container>
            <Row className="justify-content-center">
              <Col lg="10" className="video-col">
                <div className="background-black-90 rounded-top p-4">
                  <h1 className="section-heading text-h3 mb-3">
                    Thank You for Joining the Free Eat the Rainbow Challenge!
                  </h1>
                  <p className="balance-text">
                    In a few minutes, you’ll get an email with your next steps. Be sure to check your inbox!
                  </p>
                  <p className="balance-text">
                    <b>But first,</b> watch this video to find out how to get the most out of this challenge AND get a{' '}
                    <i>special, one-time opportunity</i> you’ll only see on this page.
                  </p>
                </div>
              </Col>
            </Row>
            <Row className="d-flex justify-content-center">
              <Col lg="10" className="video-col">
                <div className="background-red text-white p-3 text-uppercase text-center text-16">
                  <p className="balance-text m-0">
                    <FontAwesomeIcon icon={faArrowDown} className="mr-2" style={{maxHeight: '20px'}} /> You Will Only
                    See This Page Once — Watch The Video Below.{' '}
                    <FontAwesomeIcon icon={faArrowDown} className="ml-2" style={{maxHeight: '20px'}} />
                  </p>
                </div>
                <ScrollingVideo
                  video={{
                    url: videoUrl,
                    label: 'WLC-LC - Welcome',
                    playing: true
                  }}
                  text={
                    <>
                      <h5 className="section-heading">
                        Join WHOLE Life Club now and take <br className="d-none d-xl-block" />
                        the next step in your health journey!
                      </h5>
                    </>
                  }
                  button={{
                    scrollToSection: 'welcome-cta-3',
                    text: 'Buy Now'
                  }}
                  noFixed={false}
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="purple" className="text-center" angle="both">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-white mb-5">Make your healthy lifestyle irresistible!</h3>
                <div className="background-white box-shadow-black-50 rounded p-4 p-lg-5 text-center">
                  <h3 className="section-heading">SPECIAL OFFER for EAT THE RAINBOW CHALLENGE PARTICIPANTS ONLY:</h3>
                  <p>It’s time to say YES to your best health.</p>
                  <p>Join WHOLE Life Club now and take the next step in your health journey!</p>
                  <h2 className="text-green mt-4 mb-3">Two weeks. One dollar. Full access.</h2>
                  <h4 className="mt-0">
                    ${dollarPrice} for 14 days, then: <del className="delete-red">${fullPriceYearly}</del> $
                    {discountPriceYearly}/year
                  </h4>
                  <WelcomeCheckoutButton className="offer-box-button" />
                  <SalesDisclaimer noGuaranteeText />
                  <h5 className="mt-4">And today only, get the Brain Power Collection as a FREE bonus!</h5>
                  <p className="balance-text text-small">
                    Join WHOLE Life Club for just ${dollarPrice} for your first 14 days. After that, you’ll be enrolled
                    for the special membership price of just ${discountPriceYearly} a year (that’s only $
                    {discountPennies} per day!) unless you cancel before your 14-day trial is up. Your purchase is
                    protected by our 60-day money-back guarantee.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="welcome-message"
          imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/184835205-lg.jpg"
          imgDescription="notepad on table with vegetables"
          color="light-gray"
          angle="none"
        >
          <Container>
            <Row>
              <Col>
                <div className="background-white px-3 py-4 px-lg-5 py-lg-5 my-3">
                  <h3 className="section-heading text-center">
                    A Message From Food Revolution Network Co-founder Ocean Robbins
                  </h3>
                  <p>
                    I’m thrilled you’ve chosen to join the Eat the Rainbow Challenge. We’re going to have so much fun
                    together!
                  </p>
                  <p>
                    Millions of people are realizing that eating a rainbow of plant-based and whole foods is the #1
                    thing we can do to reverse illness AND optimize health.
                  </p>
                  <p>And every bite matters.</p>
                  <p>
                    At Food Revolution Network, we hear stories of healing, hope, and transformation almost every day.
                  </p>
                  <p>We’ve also seen that for many people, the biggest challenge isn’t how much they know…</p>
                  <p>
                    It’s putting their goals into action — <i>consistently.</i>
                  </p>
                  <p>
                    Over time, even with the best of intentions, many people slide back into old habits and fail to make
                    the progress they really want.
                  </p>
                  <p>Once our Eat the Rainbow Challenge is complete, I don’t want that to happen to you.</p>
                  <p>
                    That’s why I’d like to invite you to join Food Revolution Network’s signature membership community,
                    WHOLE Life Club.
                  </p>
                  <p>
                    This private online membership helps you move from knowing what to do to doing what you know.
                    Easily, effortlessly, and deliciously.
                  </p>
                  <p>
                    You’ll see for yourself how good life gets when you have all the support and science-based nutrition
                    insights you need to finally stick to a healthy diet.
                  </p>
                  <p>
                    I can’t wait to hear what optimizing your food and lifestyle habits does for <i>you!</i>
                  </p>
                  <Row className="d-flex align-items-center justify-content-center mt-3">
                    <Col xs="12" lg="8" xl="9" className="mb-4 mb-lg-0 text-center text-lg-left">
                      <img
                        className="mb-2"
                        src="https://cdn.foodrevolution.org/global/team/ocean-signature-firstname.png"
                        alt="ocean signature"
                        width={1000}
                        height={249}
                        style={{maxHeight: '60px', width: 'auto'}}
                      />
                      <h3 className="mt-0 mb-2 text-uppercase">Ocean Robbins</h3>
                      <h5 className="mt-0 mb-2">Co-Founder, Food Revolution Network</h5>
                      <h5 className="mt-0 mb-2">Host, WHOLE Life Club</h5>
                    </Col>
                    <Col xs="10" sm="8" lg="4" xl="3">
                      <img
                        className="rounded-circle"
                        src="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="white" angle="both">
          <Container>
            <Row>
              <Col>
                <p className="balance-text text-center text-small mb-4">
                  <i>
                    Because you just signed up for the Eat the Rainbow Challenge, you have an exclusive opportunity to
                    join FRN’s membership community, WHOLE Life Club. Many of our WLC participants have experienced
                    profound results. Each of them is unique. And so are you! Your experiences will vary, of course.
                  </i>
                </p>
                {['wlcSylvieW', 'wlcRobin', 'wlcJuanita', 'wlcCindy'].map((id) => (
                  <TestimonialCL style="default" {...testimonialsCL[id]} key={id} />
                ))}
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="welcome-get" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center">
                  Here’s what you’ll get when you join WHOLE Life Club today:
                </h3>
              </Col>
            </Row>
            <ImageCardCL
              image="https://cdn.foodrevolution.org/eattherainbow/images/box-recipe.jpg"
              imageAlt="whole foods bowl on table"
            >
              <p className="mb-0">
                Our library of over 1,400 delicious, healthy, and easily searchable plant-based recipes, plus six
                fabulous recipes delivered to your inbox every single week.
              </p>
            </ImageCardCL>
            <ImageCardCL
              image="https://cdn.foodrevolution.org/eattherainbow/images/box-video.jpg"
              imageAlt="screenshot of action video"
            >
              <p className="mb-0">
                A new short action video from Ocean and friends every week — on today’s most critical health topics,
                plus hundreds of action videos from our entire content library.
              </p>
            </ImageCardCL>
            <ImageCardCL
              image="https://cdn.foodrevolution.org/eattherainbow/images/monthly-action-hours-headshots-square.png"
              imageAlt="headshots of four experts"
              className="objectFit ahSpeakers"
            >
              <p className="mb-0">
                Monthly “Action Hours” so you can listen in as world-class guest experts share the hottest health news
                and essential practices. (Plus, access to 65+ previous episodes!)
              </p>
            </ImageCardCL>
            <ImageCardCL
              image="https://cdn.foodrevolution.org/eattherainbow/images/box-community.jpg"
              imageAlt="diverse group of people holding hands in a circle"
            >
              <p className="mb-0">
                A lively and moderated online community — so you can celebrate wins, share breakthroughs, and learn from
                others.
              </p>
            </ImageCardCL>
            <ImageCardCL
              image="https://cdn.foodrevolution.org/eattherainbow/images/health-coaches.jpg"
              imageAlt="screenshot of cooking demo"
            >
              <p className="mb-0">
                Access to expert nutritionists and health coaches — ask questions and get answers to your pressing
                health and nutrition questions anytime!
              </p>
            </ImageCardCL>
            <ImageCardCL
              image="https://cdn.foodrevolution.org/eattherainbow/images/1026486992.jpg"
              imageAlt="blue pen marking checkbox with checkmark"
            >
              <p className="mb-0">
                Action checklists and resources so you can stay motivated and steadily make progress as you create new
                healthy habits that last a lifetime.
              </p>
            </ImageCardCL>
            <ImageCardCL
              image="https://cdn.foodrevolution.org/wlc/brain-power-collection.png"
              imageAlt="brain power collection product image"
              bgColor="purple"
              textColor="white"
              className="objectFit bonusCard"
            >
              <h5 className="section-heading text-uppercase mb-3">Free Bonus today only!</h5>
              <p>
                The Brain Power Collection — interviews with three leading brain health experts who are sharing what to
                eat and do for maximum mental health, focus, and memory power — and what to do now to prevent
                Alzheimer’s.
              </p>
              <p className="mb-0">
                Grab it right now — <b>ON THIS PAGE ONLY!</b>
              </p>
            </ImageCardCL>
          </Container>
        </SectionCL>
        <SectionCL
          id=""
          color="white"
          imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/1435001922-square.jpg"
          imgDescription="man cooking healthy dinner at home"
          imgPosition="right"
          angleBottomDisplay="lg-none"
        >
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">Here are just some of the recent topics we cover in WHOLE Life Club</h3>
                <p>You’ll get them all instantly — the moment you join!</p>
                <IconListCL>
                  <>
                    <b>Saving Money with Smart Nutrition</b> — learn how eating healthy doesn’t have to break the bank —
                    with tips to cut grocery bills, reduce medical costs, and make every bite count.
                  </>
                  <>
                    <b>Foods to Fight Cancer and Thrive in Every Cell</b> — see how it’s possible to prevent cancer and
                    even take steps that help beat it.
                  </>
                  <>
                    <b>The Hidden Dangers Putting Your Health at Risk</b> — food safety, pesticides, glyphosate, GMOs,
                    toxins: what you need to know to protect yourself and your family.
                  </>
                  <>
                    <b>Probiotics and Gut Health</b> — this is one of the newest and most promising health discoveries
                    of our time, and we share all the breaking updates as they roll in.
                  </>
                  <>
                    <b>Weight Loss and Food Freedom</b> — discover how to rewire your brain to eat what’s good for you.
                  </>
                  <>
                    <b>Stopping Heart Disease</b> — how to avoid the #1 cause of death by switching to heart-healthy
                    superfoods and adopting positive lifestyle routines you’ll love.
                  </>
                </IconListCL>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id=""
          color="white"
          imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/1179985039-square.jpg"
          imgDescription="woman carrying bowl of fresh vegetables in her garden"
          imgPosition="left"
          innerClass="sectionCL-padding-lg"
        >
          <Container>
            <Row>
              <Col>
                <IconListCL>
                  <>
                    <b>Preventing Dementia</b> — employ new breakthroughs in nutrition and lifestyle to keep your brain
                    sharp and avoid devastating brain diseases.
                  </>
                  <>
                    <b>Detoxing and Cleansing</b> — discover how to use food to heal your body naturally.
                  </>
                  <>
                    <b>Reversing Chronic Inflammation</b> — implement scientifically proven ways to reduce chronic
                    inflammation and help your body to heal.
                  </>
                  <>
                    <b>Outdoor and Indoor Gardening</b> — grow your own fresh fruits, veggies, and herbs for the most
                    delicious flavor and maximum nutrient levels (even in small spaces).
                  </>
                  <>
                    <b>Creating Joy and Dealing with Stress</b> — get truly effective mind-body techniques so you can
                    become the happiest version of yourself.
                  </>
                  <>
                    <b>Mythbusting</b> — no longer wonder whether you’re doing the right thing when it comes to soy,
                    carbs, protein, fats, and other hotly debated topics.
                  </>
                  <>
                    <b>Mental Health</b> — find out what landmark studies show us about how neuroscience and nutrition
                    can help create balance, resilience, and less suffering.
                  </>
                </IconListCL>
                <p>
                  <b>And there will be new ones for you every week!</b>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="welcome-manual"
          color="light-gray"
          imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/1350164916-square.jpg"
          imgDescription="senior mother and adult daughter smiling at a phone while at a table"
          imgPosition="right"
        >
          <h3 className="section-heading">Your instruction manual for how to be healthy</h3>
          <p>
            Overcoming obstacles to being healthy is equal parts information and inspiration. That’s why each week,
            you’ll get a short video from Ocean Robbins and other Food Revolution Network leaders where you’ll learn
            what to eat and do to take excellent care of yourself and your loved ones. With each video, you’ll get an
            easy action step so you can make steady progress toward your goal to get healthier. You get access to the
            entire library the instant you join, so you can dive right into topics that may have stumped you for years.
          </p>
        </SectionCL>
        <SectionCL id="welcome-speakers" color="white" className="text-center">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">
                  Discover what the most trusted health experts know about the latest breakthroughs in medical research
                </h3>
                <h4 className="mt-0">
                  WHOLE Life Club’s monthly Action Hours keep you up-to-date with everything you need to know now to
                  stay healthy tomorrow
                </h4>
                <h5 className="mt-0">(And you get instant access the instant you enroll!)</h5>
                <p className="balance-text">
                  To get — and stay — healthy, you need to know the truth about how your body works and what you need to
                  thrive. But constant new breakthroughs in health research make it hard to keep up with the facts.
                </p>
                <p className="balance-text">
                  Now, there’s no need to do all that research on your own. Each month, we bring in the top,
                  world-renowned health experts to share their decades of hard-won wisdom with you. As soon as you join,
                  you can dive into the entire archive. Then, tune in each month for new episodes and the latest
                  breaking health developments.
                </p>
                <p className="balance-text">
                  Armed with the right knowledge, you can soar past your current personal best health in the easiest way
                  possible.
                </p>
                <p className="balance-text">
                  No trial and error. No more confusion about what’s healthy and what’s not. Just you, making huge
                  strides, one step at a time.
                </p>
              </Col>
            </Row>
            <Row className="align-items-stretch mt-4">
              {ahSpeakers.map((speaker, i) => (
                <Col sm="6" lg="3" id={'speaker-' + speaker.id} key={speaker.id} className="mb-3">
                  <img
                    className="rounded mb-3"
                    src={speaker.image.url}
                    alt={speaker.image.alt}
                    width={speaker.image.width}
                    height={speaker.image.height}
                  />
                  <h6 className="section-heading text-base mb-2">{speaker.name}</h6>
                  <h6 className="text-400 text-small mt-0 mb-0">{speaker.presentation}</h6>
                </Col>
              ))}
            </Row>
            <Row>
              <Col>
                <h4>And dozens more!</h4>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="welcome-questions"
          color="light-gray"
          imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/1408304009-square.jpg"
          imgDescription="hands raised in a group of people"
          imgPosition="left"
        >
          <h3 className="section-heading">
            Got burning questions about nutrition or <i>YOUR</i> unique health challenge?
          </h3>
          <h4 className="mt-0">
            <i>WHOLE Life Club has answers.</i>
          </h4>
          <p className="balance-text">
            One of the best parts of WHOLE Life Club is how interactive it is! As soon as you join, you’ll have access
            to WHOLE Life Club’s team of nutritionists and health coaches ready to answer your questions. Looking to
            find out if you should take supplements — and which ones? Want to know the best foods for mental health?
            Need some sleep tips to get some Zzzzs? It’s as easy as popping into the WHOLE Life Club forum to get all
            the answers you need.
          </p>
        </SectionCL>
        <SectionCL id="welcome-recipes" color="white" className="text-center">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">Never wonder what’s for dinner again!</h3>
                <p className="balance-text">
                  Have fun exploring over a thousand nutrient-packed, plant-based recipes that you can trust will be as
                  delicious as they are healthy. If you’ve ever searched for recipes online, you know it can be hard to
                  find whole foods recipes that are truly good for you. From healthy dinners that please the whole
                  family to sweet desserts that don’t leave you with a sugar hangover, you’ll find your new favorite
                  five-star recipes. (Bonus: many are based on inexpensive ingredients and come together in minutes.)
                </p>
                <h5 className="mt-4">Choose from 1,400+ mind-blowingly delicious recipes!</h5>
              </Col>
            </Row>
            <Row className="align-items-stretch">
              {recipes.map((recipe, i) => (
                <Col lg="6" id={'recipe-' + recipe.id} key={'recipe' + i} className="mb-3">
                  <ImageCardCL image={recipe.image.url} imageAlt={recipe.image.alt} imageColLg="5" textColLg="7">
                    {recipe.name}
                  </ImageCardCL>
                </Col>
              ))}
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="welcome-brain" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center mb-3">
                  Plus, If You Join Right Now, from this Page Only — You’ll Get Our Brain Power Collection Bonus!
                </h3>
                <div className="text-center mb-4">
                  <img
                    src="https://cdn.foodrevolution.org/wlc/brain-power-collection.png"
                    alt="brain power collection product image"
                    width={960}
                    height={515}
                    style={{maxHeight: '400px', width: 'auto'}}
                  />
                </div>
                <p>
                  Whether you know a thing or two about brain health or you’ve never given it much thought,{' '}
                  <br className="d-none d-xl-block" />
                  here’s the truth:
                </p>
                <p>
                  <b>Everything you do depends on how well your brain works.</b> So taking the right steps to safeguard{' '}
                  <br className="d-none d-xl-block" />
                  your brain NOW is essential to living a long and independent life.
                </p>
                <p>
                  But like millions of people, you might not know which foods and other lifestyle habits protect your{' '}
                  <br className="d-none d-xl-block" />
                  brain so you can experience mental health, clarity, and focus — well into your golden years.
                </p>
                <p>
                  The Brain Power Collection shows you <b>how to use food to prevent Alzheimer’s and dementia.</b>{' '}
                  Discover how to <i>achieve peak brain performance</i> for a sharper memory, heightened productivity,
                  and increased focus.
                </p>
                <p>With this added bonus, you’ll get:</p>
                <IconListCL>
                  <>
                    <b>Three wildly popular interviews from past Food Revolution Summits</b> feature John Robbins
                    interviewing Dr. Dale Bredesen, Dr. Daniel Amen, and Drs. Dean and Ayesha Sherzai (includes audio
                    recordings and transcripts).
                  </>
                  <>
                    <b>Simple Action Checklists</b> to help you put <i>what you discover into action</i> in your life
                    right away. <b>These are new for everyone</b> — even past Food Revolution Summit Empowerment Package
                    owners have never seen this before!
                  </>
                </IconListCL>
                <p className="text-center balance-text mt-4">
                  <b>
                    This special gift is only available right now, from this page only, when you join WHOLE Life Club
                    for ${dollarPrice} today.
                  </b>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <CtaSection
          id="welcome-cta-2"
          headline={
            <>
              <h3 className="section-heading text-center text-white">
                Enrollment is Only Open for a Limited Time, So…
              </h3>
              <h2 className="text-center text-white mt-0">Join WHOLE Life Club Now</h2>
            </>
          }
        />
        <SectionCL id="welcome-guarantee" color="light-gray">
          <Container>
            <Row>
              <Col>
                <GuaranteeBox title={<>Our 60-Day, Money-Back, Risk-Free Guarantee</>}>
                  <p>Take up to 60 days to see what WHOLE Life Club is all about.</p>
                  <p>We guarantee you and your family will:</p>
                  <IconListCL>
                    <>
                      Gain the tools and information you need to help you eat better, enjoy more vitality, and improve
                      your health right away.
                    </>
                    <>Be empowered with the truth about the foods you eat.</>
                    <>Start meaningful progress towards YOUR healthy eating goals.</>
                  </IconListCL>
                  <p>
                    But if you do request a refund, everything you've already downloaded is still yours to keep. That’s
                    why we call our guarantee risk-free!
                  </p>
                  <p>
                    If you aren’t 100% thrilled with your purchase, you have 60 days to request a prompt, total refund.
                    Email us at <a href="mailto:support@foodrevolution.org">support@foodrevolution.org</a> or phone us
                    at (831) 824-4779. You can also hit reply to any email we send you with your request.
                  </p>
                </GuaranteeBox>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="welcome-testimonials-2" color="white">
          <Container>
            <Row>
              <Col>
                <p className="balance-text text-center text-small mb-5">
                  <i>
                    Because you just signed up for the Eat the Rainbow Challenge, you have an exclusive opportunity to
                    join FRN’s membership community, WHOLE Life Club. Many of our WLC participants have experienced
                    profound results. Each of them is unique. And so are you! Your experiences will vary, of course.
                  </i>
                </p>
                {['wlcLydiaK', 'wlcLisa', 'wlcAmeliaC'].map((key) => (
                  <TestimonialCL {...testimonialsCL[key]} key={key} />
                ))}
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="welcome-join" color="light-gray">
          <Container>
            <Row className="justify-content-center">
              <Col lg="10">
                <img
                  className="mb-4"
                  src="https://cdn.foodrevolution.org/wlc/wlc-product-image-v20220329.png"
                  alt="WHOLE Life Club product image"
                  width={1200}
                  height={500}
                />
                <h3 className="section-heading text-center mb-3">Why Should You Join Today?</h3>
                <h6 className="text-400 text-center mt-0 mb-4">
                  When you join WHOLE Life Club for just <b>${dollarPrice} today,</b> you’ll get
                </h6>
                <IconListCL>
                  <>
                    <b>Your first 14 days for only ${dollarPrice}!</b> PLUS, get a special low price — you’ll pay only $
                    {discountPriceYearly} per year after your first 14 days —{' '}
                    <i>that’s just ${discountPriceMonthly} a month.</i>
                  </>
                  <>
                    <b>Annual rates never go up</b> — As long as you remain a member, your discounted membership rate
                    will be locked in.
                  </>
                  <>
                    <b>A secured spot for you...</b> The doors only open for direct access twice each year.
                  </>
                  <>
                    <b>To support our mission</b> of healthy, ethical, sustainable food for all. In fact, for every
                    annual membership, we’ll donate to Trees for the Future, enabling them to plant another organic
                    fruit or nut tree in a low-income community.
                  </>
                </IconListCL>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="welcome-thousands"
          imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/928732898v2-square.jpg"
          imgDescription="man cooking with healthy vegetables in the kitchen"
          imgPosition="right"
          color="white"
        >
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">Save Thousands (Or More!)</h3>
                <p>An ounce of prevention really is worth a pound of cure.</p>
                <p>
                  Medical expenses are a primary cause of bankruptcy and financial devastation around the world. And in
                  America, the average insured person spends over $300,000 on healthcare in their lifetime.
                </p>
                <p>Most of those bills go to crisis care, including treating very preventable chronic conditions.</p>
                <p>Heart surgery costs as much as $150,000…</p>
                <p>A year of chemotherapy sets you back around $48,000...</p>
                <p>The average price of late-stage dementia care is $287,038...</p>
                <p>
                  Not to mention all the hidden costs of getting sick, like getting passed by for that big promotion or
                  having to take time off work altogether.
                </p>
                <p>
                  But by investing just $1 now, you can test drive step-by-step support that could save you thousands
                  and thousands of dollars in the long run. And make your life happier and healthier in the short run
                  (or the short walk, hop, or skip, if you prefer!).
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="welcome-lennon" color="light-gray" innerClass="text-center">
          <Container>
            <Row className="align-items-center justify-content-center mb-5">
              <Col xs="8" sm="6" lg="3" className="mb-3 mb-lg-0">
                <img
                  src="https://cdn.foodrevolution.org/eattherainbow/images/john-lennon-headshot.jpg"
                  alt="headshot of John Lennon"
                  width={800}
                  height={800}
                  className="rounded-circle"
                />
              </Col>
              <Col xs="12" lg="auto" className="text-lg-start">
                <p className="section-heading text-800 text-h3 mb-3">
                  <i>
                    “Life is what happens while <br className="d-none d-lg-block" />
                    you’re busy making other plans.”
                  </i>
                </p>
                <h4 className="m-0">— John Lennon</h4>
              </Col>
            </Row>
            <Row>
              <Col>
                <p className="balance-text">
                  “Life happens.” And, all too often, the first thing to get derailed when it does is your healthy
                  meals.
                </p>
                <p className="balance-text">
                  You may get busy and run out of time to fix something nutritious for yourself. You might eat for
                  comfort or snack the whole day away. Or you may just not feel inspired enough to make meaningful
                  changes to your eating habits. Sound familiar?
                </p>
                <p className="balance-text">
                  That’s why I want to personally invite you to take the next step and join WHOLE Life Club now with
                  this special offer that’s only available now.
                </p>
                <h4 className="mt-0">
                  When you join WHOLE Life Club, you’ll get instant support to help you easily and successfully
                  implement, optimize, and LOVE your healthy living lifestyle.
                </h4>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <CtaSection
          id="welcome-cta-3"
          missionHeadline={<h3 className="section-heading my-4">Support Our Mission, Too!</h3>}
          trees
          angle="none"
          order="last"
          shortened
        />
      </div>
      <ExitModal
        exitDelay={30000}
        content={
          <>
            <p>
              Are you ready to prioritize your health and well-being? Experience the transformative power of WHOLE Life
              Club!
            </p>
            <p>For a limited time, you can join for just $1.</p>
          </>
        }
        image={{
          url: 'https://cdn.foodrevolution.org/eattherainbow/images/1205365239-exit.jpg',
          alt: 'bowl with quinoa, baked sweet potato and red kale leaves',
          width: 1000,
          height: 800
        }}
        positiveButtonText={
          <>
            Yes, I want <br className="d-none d-lg-block" />a $1 trial
          </>
        }
        negativeButtonText="No Thanks"
        scrollHref="welcome-cta-3"
        scrollOffset={-100}
      />
      <Footer />
    </div>
  );
}
